/**
 * Loading Skeleton Components
 * Reusable skeleton loaders for better UX during data fetching
 */

import { Box, Card, CardContent, Skeleton, Stack } from '@mui/material';

// ==================== Table Skeleton ====================

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

export const TableSkeleton = ({ rows = 5, columns = 4 }: TableSkeletonProps) => {
  return (
    <Box>
      {/* Table Header */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, px: 2 }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} variant="text" width={`${100 / columns}%`} height={40} />
        ))}
      </Stack>
      
      {/* Table Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Stack key={rowIndex} direction="row" spacing={2} sx={{ mb: 1, px: 2 }}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} variant="text" width={`${100 / columns}%`} height={30} />
          ))}
        </Stack>
      ))}
    </Box>
  );
};

// ==================== Card Skeleton ====================

interface CardSkeletonProps {
  count?: number;
}

export const CardSkeleton = ({ count = 3 }: CardSkeletonProps) => {
  return (
    <Stack spacing={2}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent>
            <Skeleton variant="text" width="60%" height={30} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="80%" height={20} />
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

// ==================== Grid Skeleton ====================

interface GridSkeletonProps {
  items?: number;
  columns?: number;
}

export const GridSkeleton = ({ items = 6, columns = 3 }: GridSkeletonProps) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 2,
      }}
    >
      {Array.from({ length: items }).map((_, i) => (
        <Card key={i}>
          <Skeleton variant="rectangular" height={200} />
          <CardContent>
            <Skeleton variant="text" width="70%" height={25} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="50%" height={20} />
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

// ==================== List Skeleton ====================

interface ListSkeletonProps {
  items?: number;
  avatar?: boolean;
}

export const ListSkeleton = ({ items = 5, avatar = true }: ListSkeletonProps) => {
  return (
    <Stack spacing={2}>
      {Array.from({ length: items }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {avatar && <Skeleton variant="circular" width={40} height={40} />}
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={25} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="50%" height={20} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

// ==================== Form Skeleton ====================

export const FormSkeleton = () => {
  return (
    <Stack spacing={3}>
      {/* Title */}
      <Skeleton variant="text" width="40%" height={35} />
      
      {/* Form Fields */}
      {Array.from({ length: 4 }).map((_, i) => (
        <Box key={i}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
        </Box>
      ))}
      
      {/* Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
      </Stack>
    </Stack>
  );
};

// ==================== Dashboard Card Skeleton ====================

export const DashboardCardSkeleton = () => {
  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Skeleton variant="text" width="50%" height={30} />
            <Skeleton variant="circular" width={40} height={40} />
          </Box>
          <Skeleton variant="text" width="70%" height={40} />
          <Skeleton variant="text" width="40%" height={20} />
        </Stack>
      </CardContent>
    </Card>
  );
};

// ==================== Profile Skeleton ====================

export const ProfileSkeleton = () => {
  return (
    <Card>
      <CardContent>
        <Stack spacing={3} alignItems="center">
          {/* Avatar */}
          <Skeleton variant="circular" width={120} height={120} />
          
          {/* Name */}
          <Skeleton variant="text" width="60%" height={35} />
          
          {/* Email */}
          <Skeleton variant="text" width="50%" height={25} />
          
          {/* Details */}
          <Box sx={{ width: '100%' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Skeleton variant="text" width="30%" height={25} />
                <Skeleton variant="text" width="50%" height={25} />
              </Box>
            ))}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ==================== Chart Skeleton ====================

export const ChartSkeleton = () => {
  return (
    <Card>
      <CardContent>
        <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
        <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="text" width={80} height={20} />
        </Stack>
      </CardContent>
    </Card>
  );
};

// ==================== Page Skeleton ====================

export const PageSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Skeleton variant="text" width="30%" height={40} />
        <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
      </Stack>
      
      {/* Content */}
      <TableSkeleton rows={8} columns={5} />
    </Box>
  );
};
